import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

const blank = { title: "", subtitle: "", cta: "Book now", link: "/", image: "", badge: "New", sort: 0, active: true };

export default function AdminAds() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null); // null = closed; {id?: ...} = open
  const load = async () => { const { data } = await api.get("/admin/ads"); setRows(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const payload = { ...edit, sort: Number(edit.sort) || 0 };
    if (!payload.title) return toast.error("Title required");
    try {
      if (payload.id) await api.patch(`/admin/ads/${payload.id}`, payload);
      else await api.post("/admin/ads", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const inlineToggle = async (id, active) => { await api.patch(`/admin/ads/${id}`, { active }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this ad?")) return; await api.delete(`/admin/ads/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Ads / banners</h1><p className="text-slate-500 mt-1">Promotional banners shown across the site.</p></div>
        <Button data-testid="ad-new" onClick={() => setEdit({ ...blank })} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"><Plus className="w-4 h-4 mr-1" /> New ad</Button>
      </div>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>Title</TableHead><TableHead>CTA / Link</TableHead><TableHead>Sort</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`ad-row-${r.id}`}>
                <TableCell>{r.image && <img src={r.image} alt="" className="w-16 h-10 object-cover rounded" />}</TableCell>
                <TableCell><div className="font-medium">{r.title}</div><div className="text-xs text-slate-500">{r.subtitle}</div></TableCell>
                <TableCell><div>{r.cta}</div><div className="text-xs text-slate-500">{r.link}</div></TableCell>
                <TableCell>{r.sort}</TableCell>
                <TableCell><Switch checked={r.active} onCheckedChange={v => inlineToggle(r.id, v)} /></TableCell>
                <TableCell className="flex gap-2">
                  <Button data-testid={`ad-edit-${r.id}`} size="sm" variant="outline" onClick={() => setEdit({ ...r })}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No ads</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && (
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">{edit.id ? "Edit ad" : "New ad"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={edit.title || ""} onChange={e=>setEdit({...edit,title:e.target.value})}/></div>
              <div className="col-span-2"><Label>Subtitle</Label><Input value={edit.subtitle || ""} onChange={e=>setEdit({...edit,subtitle:e.target.value})}/></div>
              <div><Label>CTA</Label><Input value={edit.cta || ""} onChange={e=>setEdit({...edit,cta:e.target.value})}/></div>
              <div><Label>Link</Label><Input value={edit.link || ""} onChange={e=>setEdit({...edit,link:e.target.value})}/></div>
              <div className="col-span-2"><Label>Image URL</Label><Input value={edit.image || ""} onChange={e=>setEdit({...edit,image:e.target.value})}/></div>
              <div><Label>Badge</Label><Input value={edit.badge || ""} onChange={e=>setEdit({...edit,badge:e.target.value})}/></div>
              <div><Label>Sort</Label><Input type="number" value={edit.sort || 0} onChange={e=>setEdit({...edit,sort:e.target.value})}/></div>
              <div className="col-span-2 flex items-center justify-between"><Label>Active</Label><Switch checked={!!edit.active} onCheckedChange={v=>setEdit({...edit,active:v})}/></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button><Button data-testid="ad-save" onClick={submit} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">{edit.id ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
